import React, { useState, useRef } from "react";
import { Upload, X, FileText, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import api from "../lib/api";

/**
 * File upload component for client request forms
 * Supports drag-and-drop and click-to-upload
 * Also allows pasting a URL as fallback
 */
const FileUploadField = ({ 
  value, 
  onChange, 
  placeholder = "Upload file or paste link...",
  accept = "*/*",
  maxSize = 10 * 1024 * 1024, // 10MB default
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState(null);
  const fileInputRef = useRef(null);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      await uploadFile(files[0]);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (file) {
      await uploadFile(file);
    }
  };

  const uploadFile = async (file) => {
    if (file.size > maxSize) {
      setUploadError(`File too large. Max size: ${Math.round(maxSize / 1024 / 1024)}MB`);
      return;
    }

    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post("/client-files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      const fileUrl = response.data.url;
      setUploadedFileName(file.name);
      onChange(fileUrl);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadError(error.response?.data?.detail || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleUrlInput = (e) => {
    const url = e.target.value;
    onChange(url);
    setUploadedFileName(null);
  };

  const clearFile = () => {
    onChange("");
    setUploadedFileName(null);
    setUploadError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isUrl = value && (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("/api/"));

  return (
    <div className="space-y-2">
      {/* Upload Zone */}
      <div
        className={cn(
          "relative border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer",
          isDragging ? "border-sky-500 bg-sky-500/10" : "border-zinc-700 bg-zinc-800/50 hover:border-zinc-600",
          uploading && "pointer-events-none opacity-50"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />

        {uploading ? (
          <div className="flex flex-col items-center gap-2 py-2">
            <Loader2 className="h-6 w-6 animate-spin text-sky-400" />
            <p className="text-sm text-zinc-400">Uploading...</p>
          </div>
        ) : value && isUrl ? (
          <div className="flex items-center justify-center gap-2 py-2">
            <CheckCircle2 className="h-5 w-5 text-green-400" />
            <span className="text-sm text-green-400 truncate max-w-[200px]">
              {uploadedFileName || "File uploaded"}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                clearFile();
              }}
              className="h-6 w-6 p-0 ml-2 text-zinc-500 hover:text-red-400"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 py-2">
            <Upload className="h-6 w-6 text-zinc-500" />
            <p className="text-sm text-zinc-400">
              Drop file here or click to upload
            </p>
            <p className="text-xs text-zinc-600">
              Max size: {Math.round(maxSize / 1024 / 1024)}MB
            </p>
          </div>
        )}
      </div>

      {/* Error Message */}
      {uploadError && (
        <p className="text-xs text-red-400">{uploadError}</p>
      )}

      {/* URL Fallback */}
      {!value && (
        <div className="flex items-center gap-2">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-xs text-zinc-600">or paste link</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>
      )}
      
      {!value && (
        <Input
          type="url"
          value={value || ""}
          onChange={handleUrlInput}
          placeholder={placeholder}
          className="bg-zinc-800 border-zinc-700 text-zinc-50 text-sm"
        />
      )}

      {/* Show current URL if it's manually entered */}
      {value && !uploadedFileName && isUrl && (
        <div className="flex items-center gap-2 text-xs">
          <FileText className="h-3 w-3 text-zinc-500" />
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sky-400 hover:text-sky-300 truncate"
            onClick={(e) => e.stopPropagation()}
          >
            {value}
          </a>
        </div>
      )}
    </div>
  );
};

export default FileUploadField;
